import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Radio,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import axios from 'axios';
import { stationNameToMount } from '../utils/mount-slug';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function Stations() {
  const { isSuperAdmin } = useAuth();
  const [stations, setStations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');
  const [hideServerInfo, setHideServerInfo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    call_sign: '',
    frequency: '',
    mode: 'single',
    is_active: true,
    icecast_enabled: false,
    icecast_host: 'localhost',
    icecast_port: 8000,
    icecast_username: 'source',
    icecast_password: '',
    crm_link_enabled: false,
  });

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/system-settings`, { withCredentials: true })
      .then((res) => {
        const d = res.data || {};
        setHideServerInfo(d.hide_server_info === true || d.hide_server_info === 'true');
      })
      .catch(() => {});
  }, []);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
      setError('Failed to load stations');
    }
  };

  const handleOpenDialog = (station = null) => {
    if (station) {
      setEditingStation(station);
      setFormData({
        name: station.name || '',
        call_sign: station.call_sign || '',
        frequency: station.frequency || '',
        mode: station.mode || 'single',
        is_active: station.is_active !== false,
        icecast_enabled: station.icecast_enabled || false,
        icecast_host: station.icecast_host || 'localhost',
        icecast_port: station.icecast_port || 8000,
        icecast_username: station.icecast_username || 'source',
        icecast_password: station.icecast_password || '',
        crm_link_enabled: station.crm_link_enabled === true,
      });
    } else {
      setEditingStation(null);
      setFormData({
        name: '',
        call_sign: '',
        frequency: '',
        mode: 'single',
        is_active: true,
        icecast_enabled: false,
        icecast_host: 'localhost',
        icecast_port: 8000,
        icecast_username: 'source',
        icecast_password: '',
        crm_link_enabled: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStation(null);
    setError('');
  };

  const handleSave = async () => {
    try {
      setError('');
      
      if (!formData.name) {
        setError('Station name is required');
        return;
      }

      if (editingStation) {
        await axios.put(
          `${API_URL}/api/stations/${editingStation.id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/stations`,
          formData,
          { withCredentials: true }
        );
      }

      handleCloseDialog();
      fetchStations();
    } catch (error) {
      console.error('Error saving station:', error);
      setError(error.response?.data?.message || 'Failed to save station');
    }
  };

  const handleDelete = async (stationId) => {
    if (!window.confirm('Are you sure you want to delete this station?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/stations/${stationId}`, {
        withCredentials: true
      });
      fetchStations();
    } catch (error) {
      console.error('Error deleting station:', error);
      alert('Failed to delete station');
    }
  };

  return (
    <Box>
      <PageHeader
        overline="Administration"
        title="Station Management"
        subtitle="Create and configure the stations on your suite"
        icon={<Radio />}
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
          >
            New Station
          </Button>
        }
      />

      {stations.length === 0 ? (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No stations configured. Create your first station to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {stations.map((station) => (
            <Grid item xs={12} md={6} lg={4} key={station.id}>
              <Card
                sx={{
                  bgcolor: 'var(--rs-paper)',
                  border: '1px solid var(--rs-border)',
                  '&:hover': {
                    borderColor: '#22d3ee',
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Radio sx={{ color: '#22d3ee' }} />
                        <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                          {station.name}
                        </Typography>
                      </Box>
                      {station.call_sign && (
                        <Typography variant="body2" color="text.secondary">
                          {station.call_sign}
                        </Typography>
                      )}
                      {station.frequency && (
                        <Typography variant="body2" color="text.secondary">
                          {station.frequency}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Chip
                        label={station.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: station.is_active ? '#22d3ee20' : '#666',
                          color: station.is_active ? '#22d3ee' : '#999',
                        }}
                      />
                      <Chip
                        label={station.crm_link_enabled ? 'RadioCRM' : 'Internal'}
                        size="small"
                        sx={{
                          bgcolor: station.crm_link_enabled ? '#22d3ee20' : 'transparent',
                          color: station.crm_link_enabled ? '#22d3ee' : '#999',
                          border: station.crm_link_enabled ? 'none' : '1px solid #555',
                        }}
                      />
                    </Box>
                  </Box>

                  {station.icecast_enabled && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--rs-bg)', borderRadius: 1 }}>
                      {hideServerInfo && !isSuperAdmin() ? (
                        <Typography variant="caption" color="text.secondary">
                          Stream configured · MP3 @ 320kbps
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Mount: {station.icecast_mountpoint || stationNameToMount(station.name, station.id)} · MP3 @ 320kbps
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Stream: {station.direct_stream_url || station.stream_url || `http://${station.icecast_host || 'localhost'}:${station.icecast_port || 8000}${station.icecast_mountpoint || stationNameToMount(station.name, station.id)}`}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(station)}
                      sx={{ color: '#22d3ee' }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(station.id)}
                      sx={{ color: '#ff6b6b' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Station Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Radio sx={{ color: '#22d3ee' }} />
            <Typography variant="h6">
              {editingStation ? 'Edit Station' : 'New Station'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
            <Tab label="Basic Info" />
            <Tab label="Icecast" />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Station Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Call Sign"
                  value={formData.call_sign}
                  onChange={(e) => setFormData({ ...formData, call_sign: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Frequency"
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  placeholder="e.g., 101.5 FM"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.crm_link_enabled}
                      onChange={(e) => setFormData({ ...formData, crm_link_enabled: e.target.checked })}
                    />
                  }
                  label="Allow RadioCRM"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                  Superadmin only. Leave off for internal, studio, or production stations. RadioCRM can only see stations with this on, and only through a key that lists them.
                </Typography>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.icecast_enabled}
                      onChange={(e) => setFormData({ ...formData, icecast_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Icecast Streaming"
                />
              </Grid>

              {formData.icecast_enabled && (
                <>
                  {hideServerInfo && !isSuperAdmin() ? (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Server address and port are hidden by policy. Contact a superadmin to change Icecast settings.
                      </Typography>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Icecast Host"
                          value={formData.icecast_host}
                          onChange={(e) => setFormData({ ...formData, icecast_host: e.target.value })}
                          placeholder="e.g. 217.154.59.82 or stream.example.com"
                          helperText="IP address or hostname of your Icecast server"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Port"
                          type="number"
                          value={formData.icecast_port}
                          onChange={(e) => setFormData({ ...formData, icecast_port: parseInt(e.target.value) || 8000 })}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Source Username"
                      value={formData.icecast_username}
                      onChange={(e) => setFormData({ ...formData, icecast_username: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Source Password"
                      type="password"
                      value={formData.icecast_password}
                      onChange={(e) => setFormData({ ...formData, icecast_password: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                      <Typography variant="caption" color="text.secondary">
                        Mountpoint: <strong style={{ color: '#22d3ee' }}>{formData.name ? stationNameToMount(formData.name, editingStation?.id || '?') : '(auto-derived from station name)'}</strong>
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
            {editingStation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
